import React from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faUnlock,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import Avatar from "components/base/Avatar";

interface DeleteRestoreModalProps {
  show: boolean;
  isDeleteOperation: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  itemName?: string;
  itemCode?: any;
  isLoading?: boolean;
  profileImage?: string | null;
  titleMessage?: string;
  confirmMessage?: string;
  infoMessage?: string;
  actionButtonText?: string;
}

const DeleteRestoreModal: React.FC<DeleteRestoreModalProps> = ({
  show,
  isDeleteOperation,
  onConfirm,
  onCancel,
  itemName = "este usuário",
  itemCode = "",
  isLoading = false,
  profileImage = null,
  titleMessage,
  confirmMessage,
  infoMessage,
  actionButtonText,
}) => {
  const displayName = itemCode ? `${itemName} (${itemCode})` : itemName;
  
  // Default messages
  const defaultTitleMessage = isDeleteOperation ? "Confirmar Exclusão" : "Restaurar Usuário";
  const defaultConfirmMessage = isDeleteOperation
    ? "Você está prestes a excluir o usuário:"
    : "Você está prestes a restaurar o acesso do usuário:";
  const defaultInfoMessage = isDeleteOperation
    ? "Ao excluir este usuário, ele será marcado como inativo e não poderá ser usado em outras partes do sistema. Esta ação poderá ser revertida posteriormente pelo administrador."
    : "Ao restaurar este usuário, ele voltará a ter acesso ao sistema conforme suas permissões. Todas as associações com lojas e configurações serão preservadas.";
  const defaultActionButtonText = isDeleteOperation ? "Excluir Usuário" : "Restaurar Usuário";
  
  return (
    <Modal
        show={show}
        onHide={() => onCancel()}
        centered
        className="fade"
        size="lg"
      >
        <Modal.Header className="border-0 pb-0">
          <div className="d-flex justify-content-center w-100">
            <div
              className={`rounded-circle ${
                isDeleteOperation
                  ? "bg-danger bg-opacity-10"
                  : "bg-success bg-opacity-10"
              } p-3 mb-3`}
            >
              <FontAwesomeIcon
                icon={isDeleteOperation ? faExclamationTriangle : faUnlock}
                className={`${
                  isDeleteOperation ? "text-danger" : "text-success"
                } fa-2x`}
              />
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="text-center px-4 pt-0">
          <h4 className="mb-3">
            {titleMessage || defaultTitleMessage}
          </h4>
          <p className="mb-3">
            {confirmMessage || defaultConfirmMessage}
          </p>

          <div className="d-flex align-items-center justify-content-center mb-4">
            <div className="border rounded-4 py-3 px-4 bg-body">
              <div className="d-flex align-items-center">
                <div className="rounded-circle p-2 me-3 shadow-sm">
                  {profileImage ? (
                    <Avatar variant="image" src={profileImage} size="xl">
                      {itemName?.charAt(0)}
                    </Avatar>
                  ) : (
                    <Avatar variant="name" size="xl">
                      {itemName?.charAt(0)}
                    </Avatar>
                  )}
                </div>
                <div className="text-start">
                  <h5 className="mb-0">{itemName}</h5>
                  <div className="d-flex align-items-center text-muted">
                    <small className="me-2">Código:</small>
                    <span className="badge bg-secondary">
                      {itemCode}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`bg-body border-start border-4 ${
              isDeleteOperation ? "border-warning" : "border-info"
            } rounded p-3 mb-3 text-start`}
          >
            <h6 className="d-flex align-items-center mb-2">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className={`me-2 ${
                  isDeleteOperation ? "text-warning" : "text-info"
                }`}
              />
              Informações Importantes
            </h6>
            <p className="text-muted small mb-0">
              {infoMessage || defaultInfoMessage}
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center pt-0">
          <Button
            variant="outline-secondary"
            onClick={onCancel}
            className="px-4"
          >
            Cancelar
          </Button>
          <Button
            variant={isDeleteOperation ? "danger" : "success"}
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4"
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {isDeleteOperation ? "Excluindo..." : "Restaurando..."}
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon={isDeleteOperation ? faTrash : faUnlock}
                  className="me-2"
                />
                {actionButtonText || defaultActionButtonText}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
  );
};

export default DeleteRestoreModal;
